import React, { useEffect, useState, useCallback } from "react";
import { Building2, Plus, Pencil, Trash2, Users, MessageSquare, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | company (edit)
  const [form, setForm] = useState({ name: "", industry: "", location: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/companies", { params: { search } });
    setCompanies(data);
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => { setEditing({}); setForm({ name: "", industry: "", location: "" }); setError(""); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, industry: c.industry || "", location: c.location || "" }); setError(""); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (editing && editing.id) {
        await api.put(`/companies/${editing.id}`, form);
        toast.success("Company updated");
      } else {
        await api.post("/companies", form);
        toast.success("Company added");
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    const { data } = await api.delete(`/companies/${c.id}`);
    toast.success(data.supervisors_affected > 0
      ? `Deleted — ${data.supervisors_affected} supervisor(s) still reference this name`
      : "Company deleted");
    load();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="admin-company-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search companies..." className="pl-11 h-11" />
        </div>
        <Button data-testid="admin-add-company-btn" onClick={openNew} className="bg-slate-900 hover:bg-emerald-600 rounded-full gap-2">
          <Plus size={16} /> Add Company
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-400 font-mono text-sm">Loading...</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="admin-companies-table">
          {companies.map((c) => (
            <div key={c.id} data-testid={`admin-company-row-${c.id}`} className="flex items-center gap-4 px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50">
              <div className="h-10 w-10 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
                <Building2 size={18} className="text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg text-slate-900 truncate" style={{ fontWeight: 700 }}>{c.name}</div>
                <div className="text-xs text-slate-400 font-mono">
                  {c.industry || "—"}{c.location ? ` · ${c.location}` : ""}
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-4 text-sm text-slate-500 shrink-0">
                <span className="flex items-center gap-1.5"><Users size={14} /> {c.supervisor_count}</span>
                <span className="flex items-center gap-1.5"><MessageSquare size={14} /> {c.review_count}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button data-testid={`admin-edit-company-${c.id}`} onClick={() => openEdit(c)} variant="outline" size="sm" className="h-8 gap-1 rounded-full">
                  <Pencil size={13} /> Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid={`admin-delete-company-${c.id}`} variant="ghost" size="sm" className="h-8 gap-1 rounded-full text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                      <Trash2 size={13} /> Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This removes the company from the directory. Supervisors already listed under this company keep their records.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid={`admin-confirm-delete-company-${c.id}`} onClick={() => remove(c)} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
          {companies.length === 0 && (
            <div className="px-5 py-12 text-center text-slate-400" data-testid="admin-companies-empty">No companies found.</div>
          )}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="admin-company-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2" style={{ fontWeight: 800 }}>
              <Building2 className="text-emerald-600" size={22} /> {editing && editing.id ? "Edit company" : "Add company"}
            </DialogTitle>
          </DialogHeader>
          {error && <div data-testid="admin-company-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Company name</Label>
              <Input data-testid="admin-company-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Industry</Label>
                <Input data-testid="admin-company-industry-input" value={form.industry} onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))} />
              </div>
              <div>
                <Label>Location</Label>
                <Input data-testid="admin-company-location-input" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} />
              </div>
            </div>
            <Button data-testid="admin-company-save" type="submit" disabled={saving || !form.name.trim()} className="w-full bg-slate-900 hover:bg-emerald-600 rounded-full">
              {saving ? "Saving..." : editing && editing.id ? "Save Changes" : "Add Company"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
